import { useEffect, useState } from "react";
import {
  Form,
  Input,
  DatePicker,
  Button,
  Select,
  Space,
  Row,
  Col,
  notification,
} from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { AmountInput } from "@/modules/amount-input";

import { useSaveCorrectionAndRematchMutation } from "../../../api/graphql/mutations/saveCorrectionAndRematch/saveCorrectionAndRematch.generated";
import { MatchOutcome } from "../../../api/graphql/schema.generated";
import type { EmailDocumentsPageQuery } from "../../../api/graphql/queries/emailDocumentsPage/emailDocumentsPage.generated";
import {
  DEFAULT_CURRENCY,
  DISPLAY_DATE_FORMAT,
  SUPPORTED_CURRENCIES,
} from "../../../constants";

/** Currencies offered in the correction form. EUR is the default. */
const CURRENCY_OPTIONS = SUPPORTED_CURRENCIES.map((code) => ({
  value: code,
  label: code,
}));

type ReceiptRow =
  EmailDocumentsPageQuery["emailDocumentsPage"]["items"][number];

interface CorrectionFormValues {
  name?: string;
  amount?: number;
  currency?: string;
  documentNumber?: string;
  date?: moment.Moment | null;
}

export const ReceiptCorrectionForm = ({
  emailDocument,
  publicId,
  onSaved,
}: {
  emailDocument: ReceiptRow;
  publicId: string;
  onSaved?: () => void | Promise<unknown>;
}) => {
  const { t } = useTranslation("ReceiptOverview");
  const [form] = Form.useForm<CorrectionFormValues>();
  const [isDirty, setIsDirty] = useState(false);
  const [saveCorrectionAndRematch, { loading }] =
    useSaveCorrectionAndRematchMutation();

  // Pre-fill from correctionData, falling back to the row's own fields.
  const correction = emailDocument.correctionData;
  const fallbackDate = correction?.date ?? emailDocument.date;
  // Amount is stored in cents; edit it in major units.
  const fallbackAmount = correction?.amount ?? emailDocument.amount;
  const initialValues: CorrectionFormValues = {
    name: correction?.name ?? emailDocument.name ?? undefined,
    amount: fallbackAmount == null ? undefined : fallbackAmount / 100,
    currency:
      correction?.currency ?? emailDocument.currency ?? DEFAULT_CURRENCY,
    documentNumber:
      correction?.documentNumber ?? emailDocument.documentNumber ?? undefined,
    date: fallbackDate ? moment(fallbackDate) : null,
  };

  // Re-sync the fields when the document changes (e.g. after a save refetches
  // the list and the row arrives with fresh correctionData) — Antd only applies
  // `initialValues` on first mount. Also reset the dirty flag on external updates.
  useEffect(() => {
    form.setFieldsValue(initialValues);
    setIsDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailDocument]);

  const handleValuesChange = () => {
    // Mark dirty when any field has been touched (not requiring all fields).
    setIsDirty(form.isFieldsTouched());
  };

  const handleDiscard = () => {
    form.resetFields();
    setIsDirty(false);
  };

  const handleFinish = async (values: CorrectionFormValues) => {
    try {
      const result = await saveCorrectionAndRematch({
        variables: {
          publicId,
          id: emailDocument.id,
          data: {
            name: values.name,
            // Convert major units back to cents (Int) for the API.
            amount:
              values.amount == null
                ? undefined
                : Math.round(values.amount * 100),
            currency: values.currency,
            documentNumber: values.documentNumber,
            date: values.date ? values.date.toISOString() : undefined,
          },
        },
      });

      const outcome = result.data?.saveCorrectionAndRematch?.outcome;
      if (outcome === MatchOutcome.MATCHED) {
        notification.success({ message: t("notifications.autoMatched") });
      } else {
        notification.info({ message: t("notifications.savedNoMatch") });
      }
    } catch {
      notification.error({ message: t("notifications.saveError") });
      return;
    }

    // Refresh the list so the edited row reflects the saved correction without a
    // full page reload (the client runs `no-cache`). The save already persisted,
    // so a failed refresh must not be reported as a save failure — query errors
    // are surfaced separately by the client's errorLink.
    try {
      await onSaved?.();
    } catch {
      // Intentionally ignored — see above.
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onFinish={handleFinish}
      onValuesChange={handleValuesChange}
    >
      <Form.Item name="name" label={t("form.name")}>
        <Input />
      </Form.Item>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="amount" label={t("form.amount")}>
            {/* A credit note is a legitimate negative amount here (the old
                field kept the minus); `allowNegative` is what lets it through
                instead of silently flipping it to positive. */}
            <AmountInput allowNegative style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="currency" label={t("form.currency")}>
            {/* Currency codes are short — keep the select compact instead of
                stretching it across the whole column. */}
            <Select style={{ width: 140 }} options={CURRENCY_OPTIONS} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="documentNumber" label={t("form.documentNumber")}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="date" label={t("form.date")}>
            <DatePicker className="w-full" format={DISPLAY_DATE_FORMAT} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item>
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={!isDirty}
          >
            {t("form.saveAndMatch")}
          </Button>
          <Button htmlType="button" disabled={!isDirty} onClick={handleDiscard}>
            {t("form.discardChanges")}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
